import { ComparisonOperator, ScoreArgValue } from '@/modules/surgicalEvent';
import { captureThrowOnDev } from '@/util';

export const evaluate = (
  currentVal: number | string,
  baseVal: number | string | number[],
  op: ComparisonOperator
): boolean => {
  if (op === 'in' || op === 'notin') {
    if (!Array.isArray(baseVal) || baseVal.length !== 2) {
      captureThrowOnDev(
        `Operator ${getOperatorSign(
          op
        )} needs an array of 2 elements to compare against`
      );
      return false;
    }
    const min = baseVal[0];
    const max = baseVal[1];
    if (op === 'in') {
      return (
        evaluate(currentVal, min, 'gte') && evaluate(currentVal, max, 'lte')
      );
    }
    if (op === 'notin') {
      return evaluate(currentVal, min, 'lt') || evaluate(currentVal, max, 'gt');
    }
    captureThrowOnDev(`Unknown operator: ${op}`);
    return false;
  }

  if (
    (typeof currentVal !== 'number' || typeof baseVal !== 'number') &&
    op !== 'eq' &&
    op !== 'ne'
  ) {
    captureThrowOnDev(
      `Operator ${getOperatorSign(op)} applies only to numbers`
    );
    return false;
  }
  switch (op) {
    case 'gte':
      return currentVal >= baseVal;
    case 'lte':
      return currentVal <= baseVal;
    case 'gt':
      return currentVal > baseVal;
    case 'lt':
      return currentVal < baseVal;
    case 'eq':
      return currentVal === baseVal;
    case 'ne':
      return currentVal !== baseVal;
    default:
      captureThrowOnDev(`Unknown operator: ${op}`);
      return false;
  }
};

export const createSorter =
  (op: ComparisonOperator) => (a: ScoreArgValue, b: ScoreArgValue) => {
    if (op === 'gte' || op === 'gt') {
      return b.value - a.value;
    }

    if (op === 'lte' || op === 'lt') {
      return a.value - b.value;
    }

    return 0;
  };

export const uniqueOperatorOrThrow = (arr: readonly ScoreArgValue[]) => {
  const uniq = arr[0]!.operator;
  if (arr.some((d) => d.operator !== uniq)) {
    throw new Error('Invalid state: Multiple argument values operators found');
  }

  return uniq;
};

export const getComplementaryOperator = (
  op: ComparisonOperator
): ComparisonOperator => {
  switch (op) {
    case 'gte':
      return 'lt';
    case 'lte':
      return 'gt';
    case 'gt':
      return 'lte';
    case 'lt':
      return 'gte';
    case 'eq':
      return 'ne';
    case 'ne':
      return 'eq';
    case 'in':
      return 'notin';
    case 'notin':
      return 'in';
    default:
      captureThrowOnDev(`Unknown operator: ${op}`);
      return op;
  }
};

export const getOperatorSign = (op: ComparisonOperator) => {
  switch (op) {
    case 'gte':
      return '≥';
    case 'lte':
      return '≤';
    case 'gt':
      return '>';
    case 'lt':
      return '<';
    case 'eq':
    case 'in':
      return '=';
    case 'ne':
    case 'notin':
      return '≠';
    default:
      captureThrowOnDev(`Unknown operator: ${op}`);
  }
};

export const getComplementaryOperatorSign = (op: ComparisonOperator) =>
  getOperatorSign(getComplementaryOperator(op));
